/**
 * Notification Sound Utility
 *
 * Handles playing notification sounds with proper browser autoplay handling.
 *
 * @module @municipality/chat/notificationSound
 */

const NOTIFICATION_SOUND_PATH = '/notification.wav';
const DEFAULT_VOLUME = 0.5;
const AUTOPLAY_UNLOCK_KEY = 'notification_autoplay_unlocked';
let unlockedAudioInstance: globalThis.HTMLAudioElement | null = null;
let hasWarnedAboutMissingFile = false;

export const isAutoplayUnlocked = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    return localStorage.getItem(AUTOPLAY_UNLOCK_KEY) === 'true';
  } catch {
    return false;
  }
};

export const unlockAutoplay = async (): Promise<void> => {
  if (typeof window === 'undefined') return;
  if (unlockedAudioInstance) return;
  try {
    const audio = new globalThis.Audio(NOTIFICATION_SOUND_PATH);
    audio.volume = 0;
    audio.preload = 'auto';
    await new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(() => reject(new Error('timeout')), 3000);
      audio.addEventListener('canplaythrough', () => {
        clearTimeout(timeout);
        resolve();
      }, { once: true });
      audio.addEventListener('error', () => {
        clearTimeout(timeout);
        if (audio.error?.code === 4 || audio.error?.code === 2) {
          if (!hasWarnedAboutMissingFile) {
            console.warn(
              'Notification sound not found: /notification.wav. Add to public folder.'
            );
            hasWarnedAboutMissingFile = true;
          }
        }
        reject(new Error('Audio failed'));
      }, { once: true });
      audio.load();
    });
    await audio.play();
    audio.pause();
    audio.currentTime = 0;
    audio.volume = DEFAULT_VOLUME;
    unlockedAudioInstance = audio;
    localStorage.setItem(AUTOPLAY_UNLOCK_KEY, 'true');
  } catch {
    unlockedAudioInstance = null;
    try {
      localStorage.removeItem(AUTOPLAY_UNLOCK_KEY);
    } catch {
      /**/
    }
  }
};

const checkAudioFileExists = (audio: globalThis.HTMLAudioElement): Promise<boolean> => {
  return new Promise((resolve) => {
    const timeout = setTimeout(() => resolve(false), 2000);
    audio.addEventListener('error', () => {
      clearTimeout(timeout);
      resolve(audio.error?.code !== 4 && audio.error?.code !== 2);
    }, { once: true });
    audio.addEventListener('canplay', () => {
      clearTimeout(timeout);
      resolve(true);
    }, { once: true });
    audio.load();
  });
};

export const playNotificationSound = async (volume: number = DEFAULT_VOLUME): Promise<void> => {
  if (typeof window === 'undefined') return;
  const clampedVolume = Math.max(0, Math.min(1, volume));
  if (!unlockedAudioInstance) {
    try {
      const audio = new globalThis.Audio(NOTIFICATION_SOUND_PATH);
      audio.volume = clampedVolume;
      audio.preload = 'auto';
      await audio.play();
      unlockedAudioInstance = audio;
      localStorage.setItem(AUTOPLAY_UNLOCK_KEY, 'true');
      return;
    } catch {
      return;
    }
  }
  try {
    const audio = unlockedAudioInstance;
    audio.volume = clampedVolume;
    if (audio.readyState === 0) {
      const fileExists = await checkAudioFileExists(audio);
      if (!fileExists) {
        unlockedAudioInstance = null;
        try {
          localStorage.removeItem(AUTOPLAY_UNLOCK_KEY);
        } catch {
          /**/
        }
        return;
      }
    }
    if (audio.error && (audio.error.code === 4 || audio.error.code === 2)) {
      unlockedAudioInstance = null;
      try {
        localStorage.removeItem(AUTOPLAY_UNLOCK_KEY);
      } catch {
        /**/
      }
      return;
    }
    audio.volume = clampedVolume;
    audio.currentTime = 0;
    if (audio.readyState < 2) {
      await new Promise<void>((resolve) => {
        const timeout = setTimeout(resolve, 2000);
        audio.addEventListener('canplaythrough', () => {
          clearTimeout(timeout);
          resolve();
        }, { once: true });
        audio.addEventListener('error', () => {
          clearTimeout(timeout);
          resolve();
        }, { once: true });
        if (audio.networkState === 0) audio.load();
      });
    }
    await audio.play();
  } catch (e) {
    if (e instanceof Error && e.name === 'NotAllowedError') {
      unlockedAudioInstance = null;
      try {
        localStorage.removeItem(AUTOPLAY_UNLOCK_KEY);
      } catch {
        /**/
      }
    }
  }
};

export const supportsAudio = (): boolean => {
  if (typeof window === 'undefined') return false;
  return typeof globalThis.Audio !== 'undefined';
};
